/**
 * Org-scoped ingest source badge for Control Centre tab headers.
 * Place top-right. Static label for a single source; dropdown when the org
 * has multiple telematics providers the user can filter by.
 *
 * The static expression is a `Badge` (`tone="info"`). The dropdown is a
 * control, not a badge, so it stays a feature component — but it wraps
 * `Badge`-styled markup (shared pill shape + info tone) so the two modes
 * read as one pill.
 */

import { Badge, BADGE_BASE_CLASS, TONE_CLASSES } from '../ui/badge';
import { Select } from '../ui/select';
import { cn } from '@/lib/utils';

export type ControlCentreSourceOption = {
  value: string;
  label: string;
};

interface ControlCentreSourceBadgeProps {
  /** Display label when not using a select (e.g. "Palletways") */
  label?: string | null;
  className?: string;
  /** Prefix before the source name. Default "Source:" (CC tabs); Profit uses "Data Sources:". */
  prefixLabel?: string;
  /** When length > 1, render as a Source: dropdown */
  options?: ControlCentreSourceOption[];
  value?: string;
  onChange?: (value: string) => void;
  selectId?: string;
}

/** The pill the dropdown mode wraps: `Badge` shape + info tone. */
const sourcePillClass = cn(BADGE_BASE_CLASS, TONE_CLASSES.info);

export default function ControlCentreSourceBadge({
  label,
  className = '',
  prefixLabel = 'Source:',
  options,
  value,
  onChange,
  selectId = 'control-centre-source',
}: ControlCentreSourceBadgeProps) {
  const selectable = Array.isArray(options) && options.length > 1 && typeof onChange === 'function';

  if (selectable) {
    const selectedLabel =
      options.find((o) => o.value === value)?.label
      || options[0]?.label
      || '';

    return (
      <label
        className={cn(sourcePillClass, 'cursor-pointer gap-1.5', className)}
        title={`${prefixLabel} ${selectedLabel}`}
      >
        <span className="font-semibold opacity-80">{prefixLabel}</span>
        <Select
          id={selectId}
          value={value ?? options[0]?.value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          aria-label={prefixLabel.replace(/:$/, '') || 'Data source'}
          className="max-w-56 border-0 bg-transparent pl-0 py-0 text-xs font-bold uppercase tracking-wider text-inherit hover:bg-transparent"
        >
          {options.map((opt) => (
            <option key={opt.value} value={opt.value} className="bg-popover text-white normal-case">
              {opt.label}
            </option>
          ))}
        </Select>
      </label>
    );
  }

  const text = String(label || options?.[0]?.label || '').trim();
  if (!text) return null;

  return (
    <Badge
      tone="info"
      className={cn('gap-1.5', className)}
      title={`${prefixLabel} ${text}`}
    >
      <span className="font-semibold opacity-80">{prefixLabel}</span>
      <span>{text}</span>
    </Badge>
  );
}
